/**
 * TTL-aware localStorage utilities for SSE event persistence.
 *
 * Provides utilities to store data with Time-To-Live (TTL) expiration,
 * ensuring stale data is automatically cleaned up.
 */

import { logger } from './logger';

const STORAGE_PREFIX = 'compounds-dev';

/**
 * Stored item with TTL metadata
 */
interface StoredItem<T> {
  value: T;
  expiresAt: number; // Timestamp in milliseconds
}

/**
 * Clear all app-specific items from localStorage.
 *
 * Useful for logout or reset functionality.
 *
 * @returns Number of items cleared
 *
 * @example
 * ```typescript
 * clearAll(); // Clear all app data
 * ```
 */
export function clearAll(): number {
  let cleared = 0;

  try {
    const keys = Object.keys(localStorage);
    const prefixedKeys = keys.filter(key => key.startsWith(STORAGE_PREFIX));

    for (const key of prefixedKeys) {
      localStorage.removeItem(key);
      cleared++;
    }
  } catch (error) {
    logger.error(
      { component: 'storage' },
      'Failed to clear all items',
      error instanceof Error ? error : new Error(String(error)),
    );
  }

  return cleared;
}

/**
 * Clear all expired items from localStorage.
 *
 * Scans all keys with the app prefix and removes expired items.
 * Call this on app initialization to cleanup stale data.
 *
 * @returns Number of items cleared
 *
 * @example
 * ```typescript
 * const cleared = clearExpired();
 * console.log(`Cleared ${cleared} expired items`);
 * ```
 */
export function clearExpired(): number {
  let cleared = 0;

  try {
    const keys = Object.keys(localStorage);
    const prefixedKeys = keys.filter(key => key.startsWith(STORAGE_PREFIX));

    for (const key of prefixedKeys) {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) {
          continue;
        }

        const item: StoredItem<unknown> = JSON.parse(stored);

        if (Date.now() > item.expiresAt) {
          localStorage.removeItem(key);
          cleared++;
        }
      } catch {
        // Invalid JSON, remove it
        localStorage.removeItem(key);
        cleared++;
      }
    }
  } catch (error) {
    logger.error(
      { component: 'storage' },
      'Failed to clear expired items',
      error instanceof Error ? error : new Error(String(error)),
    );
  }

  return cleared;
}

/**
 * Retrieve a value from localStorage with TTL expiration check.
 *
 * Automatically removes expired items and returns null.
 *
 * @param key - Storage key (will be prefixed)
 * @returns Stored value if valid and not expired, null otherwise
 *
 * @example
 * ```typescript
 * const prefs = getWithTTL<UserPrefs>('user-prefs');
 * if (prefs) {
 *   console.log('Theme:', prefs.theme);
 * }
 * ```
 */
export function getWithTTL<T>(key: string): T | null {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + key);
    if (!stored) {
      return null;
    }

    const item: StoredItem<T> = JSON.parse(stored);

    // Check if expired
    if (Date.now() > item.expiresAt) {
      // Remove expired item
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }

    return item.value;
  } catch (error) {
    // Parse error or localStorage unavailable
    logger.error(
      { component: 'storage', key },
      'Failed to retrieve item from localStorage',
      error instanceof Error ? error : new Error(String(error)),
    );
    return null;
  }
}

/**
 * Remove an item from localStorage.
 *
 * @param key - Storage key (will be prefixed)
 *
 * @example
 * ```typescript
 * remove('user-prefs');
 * ```
 */
export function remove(key: string): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch (error) {
    logger.error(
      { component: 'storage', key },
      'Failed to remove item from localStorage',
      error instanceof Error ? error : new Error(String(error)),
    );
  }
}

/**
 * Store a value in localStorage with TTL expiration.
 *
 * @param key - Storage key (will be prefixed)
 * @param value - Value to store (will be JSON serialized)
 * @param ttlMs - Time to live in milliseconds
 * @returns true if stored successfully, false if storage failed
 *
 * @example
 * ```typescript
 * const success = setWithTTL('user-prefs', { theme: 'dark' }, 24 * 60 * 60 * 1000);
 * ```
 */
export function setWithTTL<T>(key: string, value: T, ttlMs: number): boolean {
  try {
    const expiresAt = Date.now() + ttlMs;
    const item: StoredItem<T> = { value, expiresAt };
    const serialized = JSON.stringify(item);

    localStorage.setItem(STORAGE_PREFIX + key, serialized);
    return true;
  } catch (error) {
    // localStorage full or unavailable
    logger.error(
      { component: 'storage', key },
      'Failed to store item in localStorage',
      error instanceof Error ? error : new Error(String(error)),
    );
    return false;
  }
}
